import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Users, ShoppingCart, Package, LogOut, Moon, Sun, Globe, Store, CalendarDays, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import BrandLogo from "@/components/BrandLogo";

export default function AdminLayout() {
  const { t, lang, toggleLang, theme, toggleTheme, user, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: "/admin/customers", icon: Users, label: t.customers, key: "customers" },
    { to: "/admin/orders", icon: ShoppingCart, label: t.orders, key: "orders" },
    { to: "/admin/products", icon: Package, label: t.products, key: "products" },
    { to: "/admin/bookings", icon: CalendarDays, label: t.bookings, key: "bookings" },
    { to: "/admin/availability", icon: Clock, label: t.availability, key: "availability" },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <aside className="w-64 flex-shrink-0 border-e border-border bg-surface flex flex-col">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <BrandLogo size={36} />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold">{t.brand}</span>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">
              {t.dashboard}
            </span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const active = location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`admin-nav-${item.key}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <Link
            to="/"
            data-testid="admin-back-to-storefront"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Store className="h-4 w-4" />
            {t.backToStorefront}
          </Link>
          <button
            data-testid="admin-logout"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t.signOut}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="h-16 border-b border-border bg-surface flex items-center px-8 gap-3">
          <h2 className="text-base font-semibold">
            {t.welcome}, <span className="text-primary">{user?.name}</span>
          </h2>
          <div className="ms-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              data-testid="admin-language-switcher"
              className="gap-1.5"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm">{t.language}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="admin-darkmode-toggle"
              aria-label={t.darkMode}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
